










































import {
  computed,
  defineComponent,
  ref,
  toRaw,
  watch
} from '@vue/composition-api';
import { useVModel } from '@vueuse/core';

import { ALForm } from '@/common/component.types';
import { deepEqual } from '@/utils/helpers';
import { cloneDeep } from 'lodash';

export default defineComponent({
  name: 'FormFieldInlineEditor',
  props: {
    value: {
      type: Boolean,
      default: false
    },

    originalData: {
      type: [Object, Number, String, Boolean],
      default: null
    },

    disabled: {
      type: Boolean,
      default: false
    },

    persistent: {
      type: Boolean,
      default: false
    },

    loading: {
      type: Boolean,
      default: false
    }
  },

  emits: {
    cancel: () => true,
    save: (value: any) => true,
    input: (value: any) => true,
    'update:originalData': (value: any) => true
  },

  setup(props, { emit }) {
    const isEditing = useVModel(props, 'value', emit, {
      eventName: 'input',
      passive: true
    });

    const model = useVModel(props, 'originalData', emit, { passive: true });

    const internalModel = ref(cloneDeep(toRaw(model.value)));

    function setInternalModel(value: any) {
      internalModel.value = cloneDeep(toRaw(value));
    }

    watch(isEditing, value => {
      if (value) {
        resetFormValidation();
        setInternalModel(model.value);
      }
    });

    watch(
      () => model.value,
      newValue => {
        setInternalModel(newValue);
      }
    );

    const toggleEdit = () => {
      isEditing.value = !isEditing.value;
    };

    const isPristine = computed(() => {
      return deepEqual(model.value, internalModel.value);
    });

    function save() {
      if (validateForm()) {
        emit('save', internalModel.value);
      }
    }

    function cancel() {
      setInternalModel(model.value);

      emit('cancel');
    }

    const isFormValid = ref(false);

    const formRef = ref<ALForm | null>(null);

    function resetFormValidation(): void {
      if (formRef.value) {
        return formRef.value.resetValidation();
      }
    }

    function validateForm(): boolean {
      let valid = false;
      if (formRef.value) {
        valid = formRef.value.validate();
      }
      return valid;
    }

    const actionsDisabled = computed(() => {
      if (typeof props.disabled === 'boolean') {
        return props.disabled;
      }

      return isPristine.value;
    });

    return {
      formRef,
      isFormValid,
      actionsDisabled,
      cancel,
      save,
      isEditing,
      internalModel,
      setInternalModel,
      toggleEdit
    };
  }
});
